/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/jsx-props-no-spreading */
import * as React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slide,
  TextField,
} from "@mui/material";
import { TransitionProps } from "notistack";
import { useNotification } from "../../context/notification-provider";
import { addIgnoreList } from "./service";
import { toKebabCase } from "../helpers";
import { ENTITY_TYPE_MAP, IGNORE_LIST_API_URL_MAP } from "./constants";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface AddIgnoreListDialogProps {
  entities: {
    type: string;
    label: string;
    entityType: number;
    objectKeys: string[];
  }[];
  selectedEntityType?: string;
  open: boolean;
  onClose: () => void;
}

const AddIgnoreListDialog: React.FC<AddIgnoreListDialogProps> = ({
  entities,
  selectedEntityType,
  open,
  onClose,
}) => {
  const [entityType, setEntityType] = React.useState<string>(
    selectedEntityType || "0",
  );
  const [ignoreValue, setIgnoreValue] = React.useState("");
  const [pafudprnIgnoreValue, setPafudprnIgnoreValue] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { showNotification } = useNotification();

  const currentEntityType = entities.find(
    (rt) => rt.entityType?.toString() === entityType,
  );

  const handleSubmit = async () => {
    if (!ignoreValue.trim()) return;

    setIsSubmitting(true);
    try {
      const request: any = {
        ...currentEntityType,
        type:
          IGNORE_LIST_API_URL_MAP[currentEntityType?.type || ""] ||
          toKebabCase(currentEntityType?.type || ""),
      };

      if (currentEntityType) {
        request[currentEntityType.objectKeys[0]] = ignoreValue.trim();
        if (
          currentEntityType.type === ENTITY_TYPE_MAP.LOCATIONS &&
          pafudprnIgnoreValue.trim()
        ) {
          request.pafudprn = pafudprnIgnoreValue.trim();
        }
      }

      await addIgnoreList(request);
      showNotification({
        message: "Ignore list value added successfully.",
        severity: "success",
        duration: 3000,
      });
      setIgnoreValue("");
      setEntityType("0");
      onClose();
    } catch (err) {
      showNotification({
        message: `Failed to add ignore list value. Reason: ${(err as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      slots={{ transition: Transition }}
    >
      <DialogTitle sx={{ borderBottom: 2, mb: 2, borderColor: "divider" }}>
        Add Ignore List Value
      </DialogTitle>

      <DialogContent sx={{ pb: 0 }}>
        <Box sx={{ mb: 2 }}>
          Please select the <b>entity type</b> and enter the value you want to
          add to the ignore list.
        </Box>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Entity Type</InputLabel>
          <Select
            value={entityType}
            label="Entity Type"
            onChange={(e) => setEntityType(e.target.value)}
          >
            {entities.map((type: any) => (
              <MenuItem key={type.type} value={type.entityType?.toString()}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label={currentEntityType?.objectKeys[0] || "Value"}
          value={ignoreValue}
          onChange={(e) => setIgnoreValue(e.target.value)}
          fullWidth
          size="small"
          placeholder={
            currentEntityType?.type === "vehicleCreditHireClientIds"
              ? "Enter client ID"
              : `Enter ${currentEntityType?.label.toLowerCase()}`
          }
        />
        {currentEntityType?.type === "locations" && (
          <TextField
            sx={{ mt: 2 }}
            label="pafudprn"
            value={pafudprnIgnoreValue}
            onChange={(e) => setPafudprnIgnoreValue(e.target.value)}
            fullWidth
            size="small"
            placeholder="Pafudprn"
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!ignoreValue.trim() || isSubmitting}
        >
          Add Ignore List Value
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddIgnoreListDialog;
